// Werkliefervertrag PV (seit 30.10.2026): neutrale Vorlage für den Vertrag
// zwischen dem Käufer einer PV-Anlage (Auftraggeber) und dem Errichter
// (Auftragnehmer). Vorbild war ein Vertrag mit WOLF new energy; EN:POWER ist
// nicht Partei – deshalb ohne Logo (Entscheidungen des Nutzers, 30.10.2026).
//
// - Auftraggeber: die Käuferfelder des Studios (wie beim Projektrechtevertrag,
//   Standardkunden speicherbar). Der Vertrag nutzt nur Name/Firma und Anschrift.
// - Auftragnehmer („Verkäufer“) und Gerichtsort: Auswahl fester Verkäufer im
//   Studio (AUFTRAGNEHMER_VORGABEN) oder von Hand – seit dem 30.10.2026 nicht
//   mehr im Mapping (Wunsch des Nutzers: „aus festen Verkäufern wie EN:POWER
//   oder WOLF new energy wählen“). Der Gerichtsort hängt am Auftragnehmer.
// - Datum des Angebots: steht in keinem Bitrix-Feld, der Bearbeiter trägt es
//   im Studio ein (wie die Angaben der Aufhebung) – nicht ins Mapping.
// - Anschrift der Fläche und kWp kommen aus Bitrix über eine feste
//   Grundzuordnung (WERKVERTRAG_GRUNDZUORDNUNG): Sie gilt, solange das
//   gespeicherte Mapping für ein Feld nichts sagt. Das Mapping liegt in
//   contracts-am und war aus der Cloud-Sitzung nicht beschreibbar.
// - Hochgeladen wird nicht: Jeder andere Vertrag landet in „Pachtvertrag
//   Entwurf PDF/HTML“ und ersetzte dort den Pachtvertrag des Deals. Der fertige
//   Werkvertrag wird als PDF oder HTML heruntergeladen. Der Server lehnt den
//   Upload ebenso ab (api/_lib/werkvertrag.js).
// - Wahlbaustein „EEG-Inbetriebnahme absichern“ mit zwei Terminen: EEG_OPTION.

import { normalizePlaceholderName, replacePlaceholderWithManualValue } from './vertragsStand';
import { datumText, heuteIso } from './aufhebung';

export const WERKVERTRAG_SCHLUESSEL = 'werkliefervertrag-pv';

export const WERKVERTRAG_PLATZHALTER = {
  angebotsdatum: 'Datum Angebot',
  firma: 'Firma Auftragnehmer',
  strasse: 'Straße Auftragnehmer',
  plzOrt: 'PLZ Ort Auftragnehmer',
  vertreter: 'Vertreter Auftragnehmer',
  funktion: 'Funktion Vertreter Auftragnehmer',
  gerichtsort: 'Gerichtsort',
};

/** Die Käuferfelder, die der Werkvertrag nutzt – der Block im Studio zeigt nur diese. */
export const AUFTRAGGEBER_FELDER = ['label', 'company', 'street', 'postalCity'];

/** Die Angaben zum Auftragnehmer in der Reihenfolge der Eingabefelder. */
export const AUFTRAGNEHMER_FELDER = [
  ['firma', 'Firma'],
  ['strasse', 'Straße und Hausnummer'],
  ['plzOrt', 'PLZ und Ort'],
  ['vertreter', 'Vertreten durch (Name)'],
  ['funktion', 'Funktion, z. B. Geschäftsführer'],
  ['gerichtsort', 'Gerichtsort (§ 11.2)'],
];

const LEERER_AUFTRAGNEHMER = Object.fromEntries(AUFTRAGNEHMER_FELDER.map(([feld]) => [feld, '']));

/** Auswahl „von Hand“ statt eines festen Verkäufers. */
export const EIGENER_AUFTRAGNEHMER = 'eigener';

// Feste Verkäufer (Nutzer, 30.10.2026). EN:POWER wie in den eigenen Vorlagen
// (Gerichtsstand Magdeburg), WOLF new energy wie im Vertrag, aus dem die
// Vorlage entstand. Ein weiterer fester Verkäufer ist ein Eintrag mehr hier.
export const AUFTRAGNEHMER_VORGABEN = [
  {
    id: 'enpower',
    label: 'EN:POWER (Kirsten und Klein GbR)',
    firma: 'Kirsten und Klein GbR | EN:POWER',
    strasse: 'Halberstädter Straße 21',
    plzOrt: '39112 Magdeburg',
    vertreter: 'Randy Wesley Kirsten und Lee-Ann Sophie Klein',
    funktion: 'Gesellschafter',
    gerichtsort: 'Magdeburg',
  },
  {
    id: 'wolf-new-energy',
    label: 'WOLF new energy GmbH',
    firma: 'WOLF new energy GmbH',
    strasse: 'Beethovenstr. 4a',
    plzOrt: '06749 Bitterfeld-Wolfen',
    vertreter: 'Peter Gallmüller',
    funktion: 'Geschäftsführer',
    gerichtsort: 'Bitterfeld-Wolfen',
  },
];

// Wahlbaustein „EEG-Inbetriebnahme absichern“ (seit 30.10.2026, Wunsch des
// Nutzers: absichern, dass die Anlage „dieses Jahr noch gebaut wird“ – „es geht
// lediglich um die EEG Technische Inbetriebnahme“). Der Text steht in der
// Vorlage, jeder Absatz mit data-werkvertrag-option="eeg-inbetriebnahme":
// Vorbemerkung (6) (worauf es dem Auftraggeber ankommt) und § 5.4–5.9 (Termine,
// was Inbetriebnahme heißt, Protokoll, Verlängerung, Vertragsstrafe, Rücktritt). Alle
// stehen am Ende ihres Abschnitts – ohne Häkchen fallen sie weg, ohne Lücke in
// der Nummerierung. Zahlungsplan (§ 4) und Abnahme (§ 6) bleiben unberührt.
export const EEG_OPTION = 'eeg-inbetriebnahme';

export const EEG_PLATZHALTER = {
  montagebeginn: 'Termin Montagebeginn',
  inbetriebnahme: 'Termin EEG-Inbetriebnahme',
  jahr: 'Jahr EEG-Inbetriebnahme',
};

export const LEERE_WERKVERTRAG_ANGABEN = {
  angebotsdatum: '',
  verkaeufer: '',
  auftragnehmer: LEERER_AUFTRAGNEHMER,
  eegAbsicherung: false,
  montagebeginn: '',
  eegInbetriebnahme: '',
};

/**
 * Anschrift der Fläche und installierte Leistung aus Bitrix. Dieselben Felder
 * wie „Vorschläge einsetzen“ (MAPPING_VORSCHLAEGE; kWp = PV*SOL „Gesamt kWp“).
 */
export const WERKVERTRAG_GRUNDZUORDNUNG = {
  'Straße Hausnummer Fläche': 'UF_CRM_1752318833571',
  'PLZ, Ort Fläche': 'UF_CRM_1752313081525',
  'Projektleistung kWp': 'UF_CRM_1750195757',
};

/** Zur Zusammenfassung „das Abschließen fehlt …“ – der Vertrag wird nie hochgeladen. */
export const NICHT_HOCHLADEN_HINWEIS = 'Der Werkliefervertrag wird nicht in Bitrix hochgeladen – bitte als PDF oder HTML herunterladen.';

const text = (wert) => String(wert ?? '').trim();

export function istWerkvertrag(vertragKey) {
  return text(vertragKey) === WERKVERTRAG_SCHLUESSEL;
}

/**
 * Neue Angaben nach der Wahl eines Verkäufers. Ein fester Verkäufer bringt
 * alle Felder mit; „von Hand“ behält, was schon von Hand eingetragen war.
 */
export function waehleVerkaeufer(angaben, id) {
  const bisher = { ...LEERE_WERKVERTRAG_ANGABEN, ...(angaben || {}) };
  const vorgabe = AUFTRAGNEHMER_VORGABEN.find((v) => v.id === id);
  if (vorgabe) {
    const auftragnehmer = Object.fromEntries(AUFTRAGNEHMER_FELDER.map(([feld]) => [feld, vorgabe[feld]]));
    return { ...bisher, verkaeufer: id, auftragnehmer };
  }
  if (id === EIGENER_AUFTRAGNEHMER) {
    const behalten = bisher.verkaeufer === EIGENER_AUFTRAGNEHMER ? bisher.auftragnehmer : LEERER_AUFTRAGNEHMER;
    return { ...bisher, verkaeufer: id, auftragnehmer: { ...LEERER_AUFTRAGNEHMER, ...behalten } };
  }
  return { ...bisher, verkaeufer: '', auftragnehmer: LEERER_AUFTRAGNEHMER };
}

/** Die Funktion des Vertreters darf fehlen – dann steht nur der Name im Vertrag. */
export function auftragnehmerVollstaendig(angaben) {
  const an = angaben?.auftragnehmer || {};
  return AUFTRAGNEHMER_FELDER.every(([feld]) => feld === 'funktion' || Boolean(text(an[feld])));
}

const ISO_DATUM = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Vorschlag beim Setzen des Häkchens: Montagebeginn 23.11., Inbetriebnahme
 * 15.12. des laufenden Jahres – Luft bis Weihnachten. Ein Tag, der schon
 * vorbei ist, bleibt leer.
 */
export function eegVorschlag(heute = heuteIso()) {
  const jahr = String(heute).slice(0, 4);
  const kuenftig = (tag) => (tag >= heute ? tag : '');
  return { montagebeginn: kuenftig(`${jahr}-11-23`), eegInbetriebnahme: kuenftig(`${jahr}-12-15`) };
}

/** Häkchen an oder aus. An: leere Termine bekommen den Vorschlag, Eingetragenes bleibt. */
export function schalteEegAbsicherung(angaben, an, heute = heuteIso()) {
  const bisher = { ...LEERE_WERKVERTRAG_ANGABEN, ...(angaben || {}) };
  if (!an) return { ...bisher, eegAbsicherung: false };
  const vorschlag = eegVorschlag(heute);
  return {
    ...bisher,
    eegAbsicherung: true,
    montagebeginn: text(bisher.montagebeginn) || vorschlag.montagebeginn,
    eegInbetriebnahme: text(bisher.eegInbetriebnahme) || vorschlag.eegInbetriebnahme,
  };
}

/** Warum die Termine so nicht in den Vertrag können, sonst ''. Ohne Häkchen immer ''. */
export function eegTerminFehler(angaben) {
  if (!angaben?.eegAbsicherung) return '';
  const beginn = text(angaben.montagebeginn);
  const inbetriebnahme = text(angaben.eegInbetriebnahme);
  if (!ISO_DATUM.test(beginn) || !ISO_DATUM.test(inbetriebnahme)) return 'Montagebeginn und Inbetriebnahme eintragen.';
  if (beginn > inbetriebnahme) return 'Der Montagebeginn liegt nach der Inbetriebnahme.';
  return '';
}

export function werkvertragVollstaendig(angaben) {
  return Boolean(text(angaben?.angebotsdatum)) && auftragnehmerVollstaendig(angaben) && !eegTerminFehler(angaben);
}

/** Was im Werkvertrag ersetzt wird. Leere Angaben ersetzen nichts. */
export function werkvertragsFelder(angaben) {
  const an = angaben?.auftragnehmer || {};
  const felder = { [WERKVERTRAG_PLATZHALTER.angebotsdatum]: datumText(angaben?.angebotsdatum) };
  AUFTRAGNEHMER_FELDER.forEach(([feld]) => { felder[WERKVERTRAG_PLATZHALTER[feld]] = text(an[feld]); });
  if (angaben?.eegAbsicherung) {
    const inbetriebnahme = text(angaben.eegInbetriebnahme);
    felder[EEG_PLATZHALTER.montagebeginn] = datumText(angaben.montagebeginn);
    felder[EEG_PLATZHALTER.inbetriebnahme] = datumText(inbetriebnahme);
    felder[EEG_PLATZHALTER.jahr] = ISO_DATUM.test(inbetriebnahme) ? inbetriebnahme.slice(0, 4) : '';
  }
  return felder;
}

// Ein Absatz des Wahlbausteins samt Einrückung und Zeilenende. Absätze sind
// <p> und enthalten kein weiteres <p>; auch die Kopien in #contract-pages
// tragen das Merkmal und fallen mit weg.
const EEG_ABSATZ = /[ \t]*<p\b[^>]*\bdata-werkvertrag-option\s*=\s*(["'])eeg-inbetriebnahme\1[^>]*>[\s\S]*?<\/p>[ \t]*(?:\r?\n)?/gi;
const EEG_MERKMAL = /\bdata-werkvertrag-option\s*=\s*(["'])eeg-inbetriebnahme\1/i;

/** Steht der Wahlbaustein in diesem HTML? (Eine ältere gespeicherte Vorlage hat ihn nicht.) */
export function enthaeltEegBaustein(html) {
  return typeof html === 'string' && EEG_MERKMAL.test(html);
}

/**
 * Entfernt den Wahlbaustein, wenn er nicht gewählt ist – nur beim
 * Werkvertrag. Das Handy kennt das Häkchen nicht und ruft ohne Angaben auf.
 */
export function wendeWerkvertragOptionenAn(html, vertragKey, angaben = {}) {
  if (typeof html !== 'string' || !istWerkvertrag(vertragKey) || angaben?.eegAbsicherung) return html;
  return html.replace(EEG_ABSATZ, '');
}

const FUNKTION_NACH_KOMMA = /,\s*\{\{\s*Funktion Vertreter Auftragnehmer\s*\}\}/g;

/** Setzt die Angaben nur beim Werkvertrag ein; andere Verträge bleiben unberührt. */
export function wendeWerkvertragAn(html, vertragKey, angaben) {
  if (typeof html !== 'string' || !istWerkvertrag(vertragKey)) return html;
  let stand = wendeWerkvertragOptionenAn(html, vertragKey, angaben);
  // Ohne Funktion „vertreten durch Max Muster“ statt „…, {{Funktion …}}“.
  if (text(angaben?.auftragnehmer?.vertreter) && !text(angaben?.auftragnehmer?.funktion)) {
    stand = stand.replace(FUNKTION_NACH_KOMMA, '');
  }
  return Object.entries(werkvertragsFelder(angaben)).reduce(
    (aktuell, [platzhalter, wert]) => replacePlaceholderWithManualValue(aktuell, platzhalter, wert),
    stand
  );
}

/** Diese Platzhalter gehören nicht ins Mapping – sie kommen aus dem Studio. */
export function istWerkvertragsfeld(platzhalter) {
  const name = normalizePlaceholderName(platzhalter);
  return Object.values(WERKVERTRAG_PLATZHALTER).includes(name) || Object.values(EEG_PLATZHALTER).includes(name);
}

/** Die feste Bitrix-Zuordnung für einen Platzhalter, sonst ''. */
export function grundzuordnungFuer(vertragKey, platzhalter) {
  if (!istWerkvertrag(vertragKey)) return '';
  return WERKVERTRAG_GRUNDZUORDNUNG[normalizePlaceholderName(platzhalter)] || '';
}

/**
 * Das Mapping für die Erzeugung: Leere Felder des Werkvertrags bekommen die
 * Grundzuordnung, Eingetragenes geht vor. Andere Verträge bleiben unberührt.
 */
export function mitGrundzuordnung(vertragKey, felder = {}) {
  const basis = felder && typeof felder === 'object' ? felder : {};
  if (!istWerkvertrag(vertragKey)) return basis;
  const ergebnis = { ...basis };
  Object.entries(WERKVERTRAG_GRUNDZUORDNUNG).forEach(([platzhalter, feld]) => {
    if (!text(ergebnis[platzhalter])) ergebnis[platzhalter] = feld;
  });
  return ergebnis;
}
